import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  ForgetPasswordByCategoryReq,
  ResetPasswordRequest,
  UserRequestDTO,
  UserResponseDTO,
  UsersSearchCriteria,
  UsersSearchCriteriaRes,
  UsersSearchReqDTO
} from 'src/models/user';
import { userManagementAPIRoutes } from '..';

// Define a service using a base URL and expected endpoints
/**
 * userServices is a collection of API endpoints for user management.
 */
export const userServices = createApi({
  reducerPath: 'userServices',
  tagTypes: ['UserTag', 'VendorTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: userManagementAPIRoutes.baseUrl,

    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    /**
     * createUser is an endpoint for creating a new user.
     */
    createUser: builder.mutation<UserResponseDTO, UserRequestDTO>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.users.endpoint}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['UserTag']
    }),
    /**
     * findUserById is an endpoint for finding a user by their ID.
     */
    findUserById: builder.mutation<UserResponseDTO, number>({
      query: (id) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${id}`,
        method: 'GET'
      })
    }),
    /**
     * updateUser is an endpoint for updating a user.
     */
    updateUser: builder.mutation<
      UserResponseDTO,
      { body: UserRequestDTO; id: number }
    >({
      query: ({ body, id }) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${id}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['UserTag']
    }),
    /**
     * deleteUser is an endpoint for deleting a user.
     */
    deleteUser: builder.mutation<UserResponseDTO, number>({
      query: (id) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['UserTag']
    }),
    /**
     * getUsers is an endpoint for getting all users.
     */
    getUsers: builder.query<UsersSearchCriteriaRes, UsersSearchCriteria>({
      query: (params) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${params.page}/${params.offset}`,
        method: 'GET',
        params: {
          columnName: params.columnName,
          orderBy: params.orderBy
        }
      }),

      providesTags: ['UserTag']
    }),
    /**
     * findUsersByCategory is an endpoint for getting all users by category.
     */
    findUsersByCategory: builder.query<
      UsersSearchCriteriaRes,
      UsersSearchReqDTO
    >({
      query: (params) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${userManagementAPIRoutes.users.findUsersByCategory}`,
        method: 'POST',
        body: params
      }),
      providesTags: ['UserTag', 'VendorTag']
    }),
    forgotPasswordByCategory: builder.mutation<
      boolean,
      ForgetPasswordByCategoryReq
    >({
      query: (params) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${userManagementAPIRoutes.users.forgotPassword}/${params.email}/${params.CategoryType}`,
        method: 'PUT'
      })
    }),
    resetPassword: builder.mutation<boolean, ResetPasswordRequest>({
      query: (body) => ({
        url: `${userManagementAPIRoutes.users.endpoint}/${userManagementAPIRoutes.users.resetPassword}`,
        method: 'POST',
        body
      })
    })
  })
});

export const {
  useCreateUserMutation,
  useFindUserByIdMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetUsersQuery,
  useFindUsersByCategoryQuery,
  useForgotPasswordByCategoryMutation,
  useResetPasswordMutation
} = userServices;
